import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Calendar } from 'primereact/calendar';
import '../../css/Pages.css';

const AnimatedCardWrapper = ({ isHidden, children }) => {
    return (
        <div style={{
            flex: isHidden ? '0 0 0px' : '1 1 220px',
            maxWidth: isHidden ? '0px' : '100%',
            margin: isHidden ? '0px' : '0 10px 20px 10px',
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            zIndex: isHidden ? 0 : 1,
        }}>
            <div style={{
                position: isHidden ? 'absolute' : 'relative',
                top: 0, left: 0,
                width: '100%',
                minWidth: '220px',
                height: '100%',
                opacity: isHidden ? 0 : 1,
                transform: isHidden ? 'scale(0.9) translateX(-20px)' : 'scale(1) translateX(0)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: isHidden ? 'none' : 'auto'
            }}>
                {children}
            </div>
        </div>
    );
};

const KpiCardItem = ({ card, onClick, actionLabel, actionIcon }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
            style={{
                backgroundColor: card.bg,
                padding: '20px',
                borderRadius: '12px',
                height: '100%',
                boxSizing: 'border-box',
                boxShadow: isHovered
                    ? '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)'
                    : '0 2px 4px rgba(0,0,0,0.05)',
                transform: isHovered ? 'translateY(-5px)' : 'translateY(0)',
                transition: 'all 0.3s ease, opacity 0.3s ease',
                position: 'relative',
                cursor: onClick ? 'pointer' : 'default',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <i className={`pi ${card.icon}`} style={{ fontSize: '1.5rem', color: card.color }}></i>

                {actionLabel ? (
                    <div onClick={(e) => { e.stopPropagation(); if (card.onActionClick) card.onActionClick(); }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: isHovered ? card.color : 'transparent',
                            color: isHovered ? '#fff' : card.color,
                            padding: '4px 12px',
                            borderRadius: '20px',
                            border: `1px solid ${isHovered ? 'transparent' : card.color}`,
                            transition: 'all 0.3s ease',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap'
                        }}>
                        <span>{actionLabel}</span>
                        <i className={`pi ${actionIcon}`}></i>
                    </div>
                ) : (
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: isHovered ? card.color : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease'
                    }}>
                        <i className={isHovered ? "pi pi-arrow-right" : "pi pi-arrow-up-right"}
                            style={{ color: isHovered ? '#fff' : '#888', fontSize: '14px', transition: 'all 0.3s ease' }}>
                        </i>
                    </div>
                )}
            </div>

            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', marginBottom: '20px', minHeight: '40px', whiteSpace: 'nowrap' }}>
                {card.title}
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: '28px', fontWeight: '900', color: '#111827' }}>
                    {card.count.toLocaleString('vi-VN')}
                </span>
            </div>

            <div style={{ display: 'flex', gap: '5px', marginTop: '15px' }}>
                {[1, 2, 3, 4].map(i => (
                    <div key={i} style={{
                        height: '6px',
                        width: '20%',
                        backgroundColor: card.color,
                        opacity: isHovered ? 0.4 : 0.2,
                        borderRadius: '3px',
                        transition: 'opacity 0.3s ease'
                    }}></div>
                ))}
            </div>
        </div>
    );
};

const KpiCardGroup = ({ cards, selectedYear }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const articleCard = cards.find(c => c.detailStats) || { count: 0, detailStats: { intl: 0, domestic: 0 } };
    const otherCards = cards.filter(c => !c.detailStats);

    const queryStr = selectedYear ? `?year=${selectedYear}` : '';

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', margin: '0 -10px', marginBottom: '10px', alignItems: 'stretch' }}>
            <AnimatedCardWrapper isHidden={false}>
                <KpiCardItem
                    card={{
                        title: isExpanded ? 'Bài báo Quốc tế' : 'Bài báo khoa học',
                        count: isExpanded ? articleCard.detailStats.intl : articleCard.count,
                        bg: '#e0f2fe', color: '#0284c7', icon: isExpanded ? 'pi-globe' : 'pi-file',
                        onActionClick: toggleExpand
                    }}
                    onClick={() => navigate(`/public-bai-bao${queryStr}`)}
                    actionLabel={isExpanded ? "Thu gọn" : "Mở rộng"}
                    actionIcon={isExpanded ? "pi-angle-double-left" : "pi-angle-double-right"}
                />
            </AnimatedCardWrapper>

            <AnimatedCardWrapper isHidden={!isExpanded}>
                <KpiCardItem
                    card={{
                        title: 'Bài báo Trong nước',
                        count: articleCard.detailStats.domestic,
                        bg: '#ccfbf1', color: '#0d9488', icon: 'pi-file',
                        onActionClick: toggleExpand
                    }}
                    onClick={() => navigate(`/public-bai-bao${queryStr}`)}
                    actionLabel="Thu gọn" actionIcon="pi-angle-double-left"
                />
            </AnimatedCardWrapper>

            {otherCards.map((card, index) => (
                <AnimatedCardWrapper key={`other-${index}`} isHidden={false}>
                    <KpiCardItem
                        card={card}
                        onClick={card.path ? () => navigate(`${card.path}${queryStr}`) : undefined}
                    />
                </AnimatedCardWrapper>
            ))}
        </div>
    );
};

const PublicDashboard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [activeTab, setActiveTab] = useState('Công bố');
    const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

    const [kpiFilterYear, setKpiFilterYear] = useState(new Date());
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), 0, 1));
    const [endDate, setEndDate] = useState(null);

    useEffect(() => {
        fetchPublicDashboardData();
    }, [kpiFilterYear, startDate, endDate]);

    const fetchPublicDashboardData = async () => {
        setIsLoading(true);
        const baseUrl = process.env.REACT_APP_API_BASE_URL;

        const formatDate = (date) => {
            if (!date) return '';
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        };

        let queryParams = [];
        if (kpiFilterYear) queryParams.push(`year=${kpiFilterYear.getFullYear()}`);
        if (startDate) queryParams.push(`startDate=${formatDate(startDate)}`);
        if (endDate) queryParams.push(`endDate=${formatDate(endDate)}`);

        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

        try {
            const res = await fetch(`${baseUrl}/publicdashboard${queryString}`);
            if (res.ok) {
                const result = await res.json();
                if (result.status === 'success') {
                    setDashboardData(result.data);
                }
            } else {
                console.error("Lỗi khi fetch public dashboard:", res.statusText);
            }
        } catch (error) {
            console.error("Lỗi mạng/tải public dashboard:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const kpiCards = useMemo(() => {
        if (!dashboardData) return [];

        const counts = dashboardData.kpiCounts || {};
        const types = dashboardData.articleTypes || { intl: 0, domestic: 0 };

        return [
            {
                title: 'Bài báo khoa học',
                count: counts.articles || 0,
                bg: '#e0f2fe', color: '#0284c7', icon: 'pi-file',
                detailStats: types
            },
            {
                title: 'Đề tài NCKH', count: counts.topics || 0, bg: '#dcfce7', color: '#16a34a', icon: 'pi-book',
                path: '/public-de-tai'
            },
            {
                title: 'Sách - Giáo trình', count: counts.books || 0, bg: '#ffedd5', color: '#ea580c', icon: 'pi-bookmark',
                path: '/public-sach-giao-trinh'
            },
            {
                title: 'Nội dung NCKH khác', count: counts.others || 0, bg: '#f3e8ff', color: '#9333ea', icon: 'pi-box',
                path: '/public-nckh-khac'
            }
        ];
    }, [dashboardData]);

    const publicationYearlyData = useMemo(() => {
        if (!dashboardData) return { labels: [], data: [] };

        const filteredArticles = (dashboardData.yearlyArticles || []).filter(item => parseInt(item.year) >= 2016);

        const labels = filteredArticles.map(item => item.year);
        const data = filteredArticles.map(item => item.count);
        return { labels, data };
    }, [dashboardData]);

    const publicationLineChartData = {
        labels: publicationYearlyData.labels,
        datasets: [{
            label: 'Số lượng xuất bản', data: publicationYearlyData.data, fill: true, borderColor: '#1e40af',
            backgroundColor: 'rgba(30, 64, 175, 0.1)', tension: 0.4, pointBackgroundColor: '#1e40af',
            pointBorderColor: '#ffffff', pointBorderWidth: 2, pointRadius: 6, pointHoverRadius: 8
        }]
    };

    const publicationLineChartOptions = {
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { backgroundColor: '#1e293b', padding: 12, displayColors: false } },
        scales: { x: { grid: { display: false }, ticks: { font: { weight: 'bold' } } }, y: { beginAtZero: true, border: { display: false }, grid: { color: '#e2e8f0' }, ticks: { precision: 0 } } }
    };

    const researchFieldData = useMemo(() => {
        if (!dashboardData) return { labels: [], data: [] };
        const labels = (dashboardData.fieldArticles || []).slice(0, 10).map(item => item.name);
        const data = (dashboardData.fieldArticles || []).slice(0, 10).map(item => item.count);
        return { labels, data };
    }, [dashboardData]);

    const barColors = ['#1d4ed8', '#0ea5e9', '#d97706', '#be123c', '#8b5cf6', '#10b981', '#1e3a8a', '#f59e0b', '#b45309', '#e11d48'];
    const fieldBarChartData = {
        labels: researchFieldData.labels,
        datasets: [{ label: 'Số lượng', data: researchFieldData.data, backgroundColor: researchFieldData.labels.map((_, idx) => barColors[idx % barColors.length]), borderRadius: 4, barThickness: 15 }]
    };

    const fieldBarChartOptions = {
        maintainAspectRatio: false, indexAxis: 'y',
        plugins: { legend: { display: false }, tooltip: { backgroundColor: '#1e293b', padding: 12, displayColors: false } },
        scales: { x: { beginAtZero: true, grid: { color: '#e2e8f0' }, border: { display: false }, ticks: { precision: 0 } }, y: { grid: { display: false }, ticks: { font: { weight: '600' }, color: '#334155' } } }
    };

    const orgChartData = useMemo(() => {
        if (!dashboardData) return { labels: [], data: [], total: 0 };
        const depts = dashboardData.departmentArticles || [];
        const sorted = [...depts].sort((a, b) => b.count - a.count);

        const TOP_LIMIT = 5;
        let finalLabels = [];
        let finalData = [];
        const total = depts.reduce((sum, d) => sum + d.count, 0);

        if (sorted.length > TOP_LIMIT) {
            const topDepts = sorted.slice(0, TOP_LIMIT);
            const top5Count = topDepts.reduce((sum, d) => sum + d.count, 0);

            finalLabels = topDepts.map(d => d.name);
            finalData = topDepts.map(d => d.count);
            finalLabels.push('Các đơn vị khác');
            finalData.push(total - top5Count);
        } else {
            finalLabels = sorted.map(d => d.name);
            finalData = sorted.map(d => d.count);
        }

        return { labels: finalLabels, data: finalData, total };
    }, [dashboardData]);

    const pieColors = ['#0056b3', '#e74c3c', '#d39e00', '#8b5cf6', '#17a2b8', '#6c757d'];
    const pieChartData = {
        labels: orgChartData.labels,
        datasets: [{ data: orgChartData.data, backgroundColor: orgChartData.labels.map((_, idx) => pieColors[idx % pieColors.length]), hoverBackgroundColor: orgChartData.labels.map((_, idx) => pieColors[idx % pieColors.length]) }]
    };

    const pieChartOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'right', labels: { usePointStyle: true, font: { family: 'Inter, sans-serif', size: 13 }, color: '#334155', padding: 20 } },
            tooltip: {
                backgroundColor: '#1e293b', padding: 12,
                callbacks: {
                    label: function (context) {
                        let label = context.label || '';
                        if (label) label += ': ';
                        if (context.parsed !== null) {
                            label += context.parsed + ' bài báo';
                            const percentage = Math.round((context.parsed / orgChartData.total) * 100);
                            label += ` (${percentage}%)`;
                        }
                        return label;
                    }
                }
            }
        }
    };

    const orgArticleChartData = useMemo(() => {
        if (!dashboardData) return { labels: [], data: [] };
        const depts = dashboardData.departmentArticles || [];
        const validData = [...depts].sort((a, b) => b.count - a.count).slice(0, 10);

        return {
            labels: validData.map(d => d.name),
            data: validData.map(d => d.count)
        };
    }, [dashboardData]);

    const orgArticleBarColors = ['#0056b3', '#17a2b8', '#d39e00', '#e83e8c', '#8b5cf6', '#20c997', '#0056b3', '#17a2b8', '#d39e00', '#e83e8c'];
    const orgArticleBarData = {
        labels: orgArticleChartData.labels,
        datasets: [{ label: 'Số lượng bài báo', data: orgArticleChartData.data, backgroundColor: orgArticleChartData.labels.map((_, idx) => orgArticleBarColors[idx % orgArticleBarColors.length]), borderRadius: 4, barThickness: 15 }]
    };

    const orgArticleBarOptions = {
        maintainAspectRatio: false, indexAxis: 'y',
        plugins: { legend: { display: false }, tooltip: { backgroundColor: '#1e293b', padding: 12, displayColors: false } },
        scales: { x: { beginAtZero: true, border: { display: false }, ticks: { precision: 0 } }, y: { grid: { display: false }, ticks: { font: { weight: '600' }, color: '#334155' } } }
    };

    const carouselCharts = {
        'Công bố': [
            {
                title: 'NĂM XUẤT BẢN (CÔNG BỐ NCKH)',
                content: (
                    publicationYearlyData.labels.length > 0 ? (
                        <Chart type="line" data={publicationLineChartData} options={publicationLineChartOptions} style={{ height: '100%', width: '100%' }} />
                    ) : (<div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Không có dữ liệu năm xuất bản</div>)
                )
            },
            {
                title: 'TOP 10 LĨNH VỰC NGHIÊN CỨU',
                content: (
                    researchFieldData.labels.length > 0 ? (
                        <Chart type="bar" data={fieldBarChartData} options={fieldBarChartOptions} style={{ height: '100%', width: '100%' }} />
                    ) : (<div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Không có dữ liệu lĩnh vực</div>)
                )
            }
        ],
        'Tổ chức': [
            {
                title: 'CƠ CẤU ĐƠN VỊ KHOA / PHÒNG',
                content: (
                    orgChartData.labels.length > 0 ? (
                        <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ flex: 1 }}>
                                <Chart type="pie" data={pieChartData} options={pieChartOptions} style={{ height: '100%', width: '100%' }} />
                            </div>
                            <div style={{ position: 'absolute', bottom: '10px', right: '20px', fontWeight: 'bold', fontSize: '16px', color: '#1e293b' }}>
                                Tổng: {orgChartData.total.toLocaleString('vi-VN')}
                            </div>
                        </div>
                    ) : (<div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Không có dữ liệu tổ chức</div>)
                )
            },
            {
                title: 'TOP 10 KHOA / PHÒNG (THEO CÔNG BỐ)',
                content: (
                    orgArticleChartData.labels.length > 0 ? (
                        <Chart type="bar" data={orgArticleBarData} options={orgArticleBarOptions} style={{ height: '100%', width: '100%' }} />
                    ) : (<div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Không có dữ liệu công bố theo đơn vị</div>)
                )
            }
        ]
    };

    const activeCharts = carouselCharts[activeTab] || [];

    const nextCarouselChart = () => {
        if (activeCharts.length > 0) setCurrentCarouselIndex((prev) => (prev + 1) % activeCharts.length);
    };

    const prevCarouselChart = () => {
        if (activeCharts.length > 0) setCurrentCarouselIndex((prev) => (prev - 1 + activeCharts.length) % activeCharts.length);
    };

    useEffect(() => {
        setCurrentCarouselIndex(0);
    }, [activeTab]);

    const oldBarData = useMemo(() => {
        const ov = dashboardData?.overviewCounts || { Article: {}, Topic: {}, Book: {}, Other: {} };
        return {
            labels: ['Bài báo', 'Đề tài', 'Sách - Giáo trình', 'Nội dung NCKH khác'],
            datasets: [
                { label: 'Kê khai', backgroundColor: '#3b82f6', data: [ov.Article?.keKhai || 0, ov.Topic?.keKhai || 0, ov.Book?.keKhai || 0, ov.Other?.keKhai || 0] },
                { label: 'Đã duyệt', backgroundColor: '#f59e0b', data: [ov.Article?.daDuyet || 0, ov.Topic?.daDuyet || 0, ov.Book?.daDuyet || 0, ov.Other?.daDuyet || 0] }
            ]
        };
    }, [dashboardData]);

    const oldAreaData = useMemo(() => {
        const history = (dashboardData?.allYearlyHistory || []).filter(item => parseInt(item.year) >= 2016);

        const yearSet = new Set();
        history.forEach(i => yearSet.add(i.year));
        const allYears = Array.from(yearSet).sort();

        const getCounts = (type) => allYears.map(y => {
            const found = history.find(i => i.type === type && i.year === y);
            return found ? found.count : 0;
        });

        return {
            labels: allYears,
            datasets: [
                { label: 'Bài báo', data: getCounts('Article'), fill: true, borderColor: '#3b82f6', backgroundColor: 'rgba(59, 130, 246, 0.1)', tension: 0.4 },
                { label: 'Đề tài', data: getCounts('Topic'), fill: true, borderColor: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.1)', tension: 0.4 },
                { label: 'Sách - Giáo trình', data: getCounts('Book'), fill: true, borderColor: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.1)', tension: 0.4 }
            ]
        };
    }, [dashboardData]);

    const topAuthors = useMemo(() => {
        if (!dashboardData || !dashboardData.authorArticles || !dashboardData.allUsers) return [];

        const authorMap = {};

        dashboardData.authorArticles.forEach(art => {
            let hasMembers = false;

            try {
                if (art.MembersJSON) {
                    const members = JSON.parse(art.MembersJSON);
                    if (Array.isArray(members) && members.length > 0) {
                        hasMembers = true;
                        const processedKeys = new Set();

                        members.forEach(m => {
                            const userId = m.UserId || m.userId || m.Id || m.id;
                            const rawName = m.FullName || m.fullName || m.Email || m.email || "Ẩn danh";
                            const groupKey = userId ? `id_${userId}` : `name_${rawName.toLowerCase()}`;

                            if (!processedKeys.has(groupKey)) {
                                processedKeys.add(groupKey);
                                let displayName = rawName;
                                let department = "Đơn vị khác / Ngoài trường";

                                if (userId) {
                                    const matchedUser = dashboardData.allUsers.find(u => u.Id?.toString() === userId.toString());
                                    if (matchedUser) {
                                        displayName = `${matchedUser.LastName || ''} ${matchedUser.FirstName || ''}`.trim() || rawName;
                                        department = matchedUser.DepartmentName || department;
                                    }
                                } else if (art.FullName === rawName) {
                                    department = art.DepartmentName || department;
                                }

                                if (!authorMap[groupKey]) {
                                    authorMap[groupKey] = { name: displayName, count: 0, department: department };
                                }
                                authorMap[groupKey].count += 1;
                            }
                        });
                    }
                }
            } catch (e) {
                console.warn("Lỗi parse MembersJSON:", e);
            }

            if (!hasMembers) {
                const userId = art.UserId;
                const rawName = art.FullName || art.CreatedByEmail || "Ẩn danh";
                const groupKey = userId ? `id_${userId}` : `name_${rawName.toLowerCase()}`;

                if (!authorMap[groupKey]) {
                    authorMap[groupKey] = {
                        name: rawName,
                        count: 0,
                        department: art.DepartmentName || "Đơn vị khác / Ngoài trường"
                    };
                }
                authorMap[groupKey].count += 1;
            }
        });

        return Object.values(authorMap)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [dashboardData]);

    const commonOptions = { maintainAspectRatio: false, responsive: true };
    const oldBarOptions = { ...commonOptions, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } };
    const oldAreaOptions = { ...commonOptions, plugins: { legend: { display: true } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } };

    if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', marginTop: '100px' }}><ProgressSpinner /></div>;

    const styles = {
        container: { padding: '20px', backgroundColor: '#f4f7f6', minHeight: '100vh', boxSizing: 'border-box' },
        header: { backgroundColor: '#1e3a8a', backgroundImage: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)', padding: '20px', borderRadius: '10px', color: '#fff', marginBottom: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
        row: { display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' },
        card: { flex: '1 1 45%', backgroundColor: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', minWidth: '300px', display: 'flex', flexDirection: 'column' },
        cardTitleContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' },
        cardTitleText: { margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#444', textTransform: 'uppercase' },
        dateFilterBox: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
        chartWrapper: { flex: 1, minHeight: '300px', position: 'relative' },
        authorItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' },
        avatarBox: { display: 'flex', alignItems: 'center', gap: '15px' },
        avatar: { width: '45px', height: '45px', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px' },
        badge: { width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #22c55e', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f0fdf4' }
    };

    const renderDateFilter = () => (
        <div style={styles.dateFilterBox}>
            <Calendar
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                placeholder="Từ ngày"
                dateFormat="dd/mm/yy"
                showIcon
                showClear
                style={{ width: '150px' }}
                inputStyle={{ padding: '8px', fontSize: '13px' }}
            />
            <span style={{ color: '#888', fontWeight: 'bold' }}>-</span>
            <Calendar
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                placeholder="Đến ngày"
                dateFormat="dd/mm/yy"
                showIcon
                showClear
                style={{ width: '150px' }}
                inputStyle={{ padding: '8px', fontSize: '13px' }}
                minDate={startDate || undefined}
            />
        </div>
    );

    const tabsMenu = ['Công bố', 'Tổ chức'];

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={{ margin: 0, textTransform: 'uppercase', fontSize: '22px' }}>Thông tin tổng quan</h2>
                <p style={{ margin: '5px 0 0 0', opacity: 0.9, fontSize: '16px' }}>Hệ thống quản lý khoa học</p>
            </div>

            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translate(-50%, 5px); }
                    to { opacity: 1; transform: translate(-50%, 0); }
                }
                `}
            </style>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
                <Calendar
                    value={kpiFilterYear}
                    onChange={(e) => setKpiFilterYear(e.value)}
                    view="year"
                    dateFormat="yy"
                    placeholder="Lọc theo năm"
                    showIcon
                    showClear
                    style={{ width: '180px' }}
                    inputStyle={{ padding: '8px', fontSize: '13px' }}
                />
            </div>

            <KpiCardGroup
                cards={kpiCards}
                selectedYear={kpiFilterYear ? kpiFilterYear.getFullYear() : null}
            />

            <div style={{ marginBottom: '20px', marginTop: '40px' }}>
                <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', gap: '5px' }}>
                    {tabsMenu.map(tab => (
                        <div
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '12px 24px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '15px',
                                color: activeTab === tab ? '#1d4ed8' : '#64748b',
                                borderBottom: activeTab === tab ? '3px solid #1d4ed8' : '3px solid transparent',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {tab}
                        </div>
                    ))}
                </div>
            </div>

            {activeCharts.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '30px' }}>
                    <button
                        onClick={prevCarouselChart}
                        style={{
                            background: '#ffffff', border: '1px solid #1d4ed8', borderRadius: '50%',
                            width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: '#1d4ed8', fontSize: '18px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            opacity: activeCharts.length <= 1 ? 0.3 : 1, pointerEvents: activeCharts.length <= 1 ? 'none' : 'auto'
                        }}
                    >
                        <i className="pi pi-angle-left"></i>
                    </button>

                    <div style={{
                        flex: 1,
                        maxWidth: '1000px',
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        padding: '30px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                        border: '1px solid #f1f5f9'
                    }}>
                        <h3 style={{ textAlign: 'center', color: '#0f172a', fontSize: '20px', margin: '0 0 30px 0', textTransform: 'uppercase' }}>
                            {activeCharts[currentCarouselIndex].title}
                        </h3>
                        <div style={{ height: '400px', position: 'relative' }}>
                            {activeCharts[currentCarouselIndex].content}
                        </div>
                    </div>

                    <button
                        onClick={nextCarouselChart}
                        style={{
                            background: '#ffffff', border: '1px solid #1d4ed8', borderRadius: '50%',
                            width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: '#1d4ed8', fontSize: '18px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            opacity: activeCharts.length <= 1 ? 0.3 : 1, pointerEvents: activeCharts.length <= 1 ? 'none' : 'auto'
                        }}
                    >
                        <i className="pi pi-angle-right"></i>
                    </button>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8', backgroundColor: '#fff', borderRadius: '16px', marginBottom: '30px', border: '1px solid #f1f5f9' }}>
                    Chưa có dữ liệu thống kê cho mục này
                </div>
            )}

            <hr style={{ border: 'none', borderTop: '2px dashed #e2e8f0', margin: '40px 0' }} />

            <div style={styles.row}>
                <div style={{ ...styles.card, flex: '1.5 1 55%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Số lượng công trình</h3>
                        {renderDateFilter()}
                    </div>
                    <div style={styles.chartWrapper}>
                        <Chart type="bar" data={oldBarData} options={oldBarOptions} style={{ height: '100%', width: '100%' }} />
                    </div>
                </div>

                <div style={{ ...styles.card, flex: '1 1 40%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Top 5 công bố bài báo (Đã duyệt)</h3>
                        {renderDateFilter()}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        {topAuthors.length > 0 ? topAuthors.map((author, index) => (
                            <div key={index} style={styles.authorItem}>
                                <div style={styles.avatarBox}>
                                    <div style={{ ...styles.avatar, backgroundColor: ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444'][index % 5] }}>
                                        {author.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 'bold', color: '#333', fontSize: '15px' }}>{author.name}</div>
                                        <div style={{ fontSize: '13px', color: '#888', marginTop: '3px' }}>{author.department}</div>
                                    </div>
                                </div>
                                <div style={styles.badge}>{author.count}</div>
                            </div>
                        )) : (
                            <p style={{ textAlign: 'center', color: '#888', marginTop: '20px' }}>Không có dữ liệu trong khoảng thời gian này</p>
                        )}
                    </div>
                </div>
            </div>

            <div style={styles.row}>
                <div style={{ ...styles.card, flex: '1 1 100%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Số lượng công bố khoa học qua các năm</h3>
                    </div>
                    <div style={{ height: '350px', position: 'relative' }}>
                        <Chart type="line" data={oldAreaData} options={oldAreaOptions} style={{ height: '100%', width: '100%' }} />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PublicDashboard;